import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import Icon from '../../../../atoms/Icon'
import MarkupPreview from '../../../../atoms/MarkupPreview'
import FlexRow from '../../../../molecules/FlexRow'
import CreateNoteModal from './CreateNoteModal'
import NoteDetailModal from './NoteDetailModal'

function formatNoteDate (isoString) {
  if (!isoString) return null
  return dayjs(isoString).format('M/D/YYYY')
}

function NoteCard ({ note, onClick, previewLength }) {
  const category = note.category || note.note_type_description || null
  const dateStr = formatNoteDate(note.created_at)

  return (
    <div
      role='button'
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick() } }}
      style={{
        backgroundColor: 'var(--color-neutral-100)',
        border: '1px solid var(--color-neutral-300)',
        borderRadius: '8px',
        padding: '12px',
        marginBottom: '8px',
        cursor: 'pointer'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
        <div style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          paddingTop: '2px',
          color: note.pinned ? 'var(--color-primary-400)' : 'var(--color-neutral-500)'
        }}
        >
          <Icon name={note.pinned ? 'pin' : 'unpin'} customSize='18px' />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {note._contact_name ? (
            <div style={{ fontSize: '11px', color: 'var(--text-color-subtle)', marginBottom: '2px' }}>
              {note._contact_name}
            </div>
          ) : null}
          {category ? (
            <div style={{ fontWeight: 600, fontSize: 'var(--font-size-xs)', marginBottom: '4px' }}>
              {category}
            </div>
          ) : null}
          <MarkupPreview
            type='html'
            length={previewLength}
            value={note.body || ''}
            className='font-size-sm'
          />
        </div>
        {dateStr ? (
          <div style={{
            flexShrink: 0,
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text-color-subtle)',
            whiteSpace: 'nowrap'
          }}
          >
            {dateStr}
          </div>
        ) : null}
      </div>
    </div>
  )
}

NoteCard.propTypes = {
  note: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired,
  previewLength: PropTypes.number
}

NoteCard.defaultProps = {
  previewLength: 200
}

function NotesContent ({ data, members, config, createOpen, onCloseCreate }) {
  const [selectedNote, setSelectedNote] = useState(null)

  const pinned = data?.pinned ?? []
  const recent = data?.recent ?? []
  const previewLength = config?.notePreviewLength ?? 200

  const handleCloseDetail = useCallback(() => setSelectedNote(null), [])

  return (
    <>
      {pinned.length > 0 ? (
        <div style={{ marginBottom: '12px' }}>
          <div style={{ fontWeight: 700, fontSize: 'var(--font-size-xs)', marginBottom: '6px', textTransform: 'uppercase' }}>
            Pinned
          </div>
          {pinned.map(note => (
            <NoteCard key={note.id} note={note} previewLength={previewLength} onClick={() => setSelectedNote(note)} />
          ))}
        </div>
      ) : null}

      <div>
        <div style={{ fontWeight: 700, fontSize: 'var(--font-size-xs)', marginBottom: '6px', textTransform: 'uppercase' }}>
          Recent
        </div>
        {recent.length > 0
          ? recent.map(note => (
            <NoteCard key={note.id} note={note} previewLength={previewLength} onClick={() => setSelectedNote(note)} />
          ))
          : (
            <FlexRow
              className='justify-center items-center'
              style={{
                minHeight: '60px',
                color: 'var(--text-color-subtle)',
                fontSize: 'var(--font-size-sm)'
              }}
            >
              No recent notes
            </FlexRow>
          )}
      </div>

      <CreateNoteModal
        open={createOpen}
        onClose={onCloseCreate}
        config={config}
        members={members}
        existingNotes={[...pinned, ...recent]}
      />

      <NoteDetailModal
        open={!!selectedNote}
        onClose={handleCloseDetail}
        note={selectedNote}
      />
    </>
  )
}

NotesContent.propTypes = {
  data: PropTypes.object,
  members: PropTypes.array,
  config: PropTypes.object,
  createOpen: PropTypes.bool,
  onCloseCreate: PropTypes.func
}

export default NotesContent
